import { SupabaseClient } from '@supabase/supabase-js';
import { ObservableField } from './observable-field';

const POLL_INTERVAL_MS = 3_000;

export type SocketState = 'open' | 'connecting' | 'closing' | 'closed';

/**
 * Monitors the underlying Phoenix WebSocket connection state by polling
 * the Supabase Realtime client's connectionState() every POLL_INTERVAL_MS.
 *
 * This gives a socket-level health signal independent of any specific channel,
 * complementing the per-channel status tracked by MatchChronology and Broadcast.
 */
export class RealtimeSocketMonitor {
  socketState: ObservableField<SocketState> = new ObservableField<SocketState>('closed');

  private timer?: ReturnType<typeof setInterval>;

  constructor(private supabase: SupabaseClient) {}

  public start() {
    this.poll();
    this.timer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  public stop() {
    clearInterval(this.timer);
    this.timer = undefined;
  }

  private poll() {
    const raw: string = (this.supabase as any).realtime?.connectionState?.() ?? 'closed';
    const state = this.normalize(raw);
    this.socketState.value = state;
  }

  private normalize(raw: string): SocketState {
    switch (raw) {
      case 'open':        return 'open';
      case 'connecting':  return 'connecting';
      case 'closing':     return 'closing';
      default:            return 'closed';
    }
  }
}
